/**
 * Cross-app auth handoff contract shared between frontend/ (the auth page) and any other
 * app (e.g. admin/) that needs to send an unauthenticated/unregistered user into the main
 * app's register/recovery flow and get them back without losing already-typed identity.
 */
export type AuthIdentityType = 'phone';

export const AUTH_HANDOFF_PARAM = {
  identityType: 'identity_type',
  identity: 'identity',
  redirectUrl: 'redirect_url',
} as const;

const MAX_IDENTITY_LENGTH = 32;

export interface AuthHandoffParams {
  identityType: AuthIdentityType | null;
  identity: string | null;
  redirectUrl: string | null;
}

export function readAuthHandoffParams(
  searchParams: URLSearchParams
): AuthHandoffParams {
  const identityTypeRaw = searchParams.get(AUTH_HANDOFF_PARAM.identityType);
  const identityType: AuthIdentityType | null =
    identityTypeRaw === 'phone' ? 'phone' : null;

  const identityRaw = searchParams.get(AUTH_HANDOFF_PARAM.identity);
  const identity = identityRaw ? identityRaw.slice(0, MAX_IDENTITY_LENGTH) : null;

  return {
    identityType,
    identity,
    redirectUrl: searchParams.get(AUTH_HANDOFF_PARAM.redirectUrl),
  };
}

/**
 * `redirect_url` crosses an app boundary and ends up in `window.location.href` — it must
 * never be trusted blindly. Only an exact match against a caller-supplied allowlist of
 * known app origins (env-configured per deployment) is accepted.
 */
export function isTrustedRedirectUrl(
  candidate: string | null | undefined,
  trustedOrigins: readonly string[]
): boolean {
  if (!candidate || trustedOrigins.length === 0) {
    return false;
  }

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return false;
  }

  return trustedOrigins.includes(parsed.origin);
}

export function appendAuthHandoffParams(
  url: string,
  params: { identityType?: AuthIdentityType | null; identity?: string | null }
): string {
  const target = new URL(url);

  if (params.identityType) {
    target.searchParams.set(AUTH_HANDOFF_PARAM.identityType, params.identityType);
  }

  if (params.identity) {
    target.searchParams.set(AUTH_HANDOFF_PARAM.identity, params.identity);
  }

  return target.toString();
}

export function buildAuthHandoffUrl(
  baseUrl: string,
  params: {
    identityType?: AuthIdentityType | null;
    identity?: string | null;
    redirectUrl?: string | null;
    extraParams?: Record<string, string>;
  }
): string {
  const target = new URL(baseUrl);

  if (params.identityType) {
    target.searchParams.set(AUTH_HANDOFF_PARAM.identityType, params.identityType);
  }

  if (params.identity) {
    target.searchParams.set(AUTH_HANDOFF_PARAM.identity, params.identity);
  }

  if (params.redirectUrl) {
    target.searchParams.set(AUTH_HANDOFF_PARAM.redirectUrl, params.redirectUrl);
  }

  if (params.extraParams) {
    for (const [key, value] of Object.entries(params.extraParams)) {
      target.searchParams.set(key, value);
    }
  }

  return target.toString();
}
